import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ShoppingCart, Receipt, FolderLock, Loader2, Clock, GraduationCap } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const MODULES = [
  { key: "orders",    label: "Bestellingen",     icon: ShoppingCart, sub: "Nieuwe bestelling · Bestellingen · Voorraad" },
  { key: "invoices",  label: "Facturen",         icon: Receipt,      sub: "Facturen uploaden en beheren" },
  { key: "documents", label: "Documenten",       icon: FolderLock,   sub: "Hygiëne, contracten, PDFs" },
  { key: "shifts",    label: "Shiftregistratie", icon: Clock,        sub: "Uren, pauzes en Arbeidstijdenwet — enkel voor admins" },
  { key: "training",  label: "HACCP Training",   icon: GraduationCap, sub: "Hygiënecode-toetsen met certificaat voor chefs" },
];

function Toggle({ checked, onChange, disabled, testid }) {
  return (
    <button onClick={() => !disabled && onChange(!checked)}
      disabled={disabled} data-testid={testid}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${checked ? "bg-[#5BC044]" : "bg-zinc-500/40"} disabled:opacity-50`}>
      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${checked ? "translate-x-6" : "translate-x-1"}`} />
    </button>
  );
}

export default function ModulesSettings() {
  const [rows, setRows] = useState(null);
  const [saving, setSaving] = useState({});
  const [q, setQ] = useState("");

  const load = async () => {
    const r = await api.get("/platform/restaurant_modules");
    setRows(r.data);
  };
  useEffect(() => { load(); }, []);

  const toggle = async (row, mod, next) => {
    const key = `${row.restaurant_id}:${mod}`;
    setSaving(s => ({ ...s, [key]: true }));
    setRows(rs => rs.map(x => x.restaurant_id === row.restaurant_id ? { ...x, [mod]: next } : x));
    try {
      await api.put("/platform/restaurant_modules", { restaurant_id: row.restaurant_id, [mod]: next });
      toast.success(`${MODULES.find(m => m.key === mod)?.label} · ${next ? "aan" : "uit"} voor ${row.restaurant_name}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Opslaan mislukt");
      setRows(rs => rs.map(x => x.restaurant_id === row.restaurant_id ? { ...x, [mod]: !next } : x));
    } finally {
      setSaving(s => { const c = { ...s }; delete c[key]; return c; });
    }
  };

  const filtered = (rows || []).filter(r =>
    !q.trim() ||
    r.restaurant_name.toLowerCase().includes(q.toLowerCase()) ||
    (r.organization_name || "").toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="min-h-screen bg-[#0F1730] text-white">
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <Link to="/platform" className="inline-flex items-center gap-2 text-xs text-white/60 hover:text-white mb-2">
              <ArrowLeft size={13} /> Terug naar dashboard
            </Link>
            <h1 className="text-2xl font-bold">Modules — toegang per restaurant</h1>
            <p className="text-xs text-white/60 mt-1">Kies per restaurant welke modules zichtbaar zijn in hun account. Uitgezette modules verdwijnen uit het menu en dashboard.</p>
          </div>
          <input value={q} onChange={e => setQ(e.target.value)}
            placeholder="Zoek restaurant of organisatie…"
            className="w-72 bg-white/5 border border-white/10 rounded-md h-9 px-3 text-sm placeholder-white/40"
            data-testid="modules-search" />
        </div>

        {rows === null ? (
          <div className="flex items-center justify-center py-16 text-white/60">
            <Loader2 className="animate-spin mr-2" size={16} /> Laden…
          </div>
        ) : (
          <div className="bg-white/[0.03] border border-white/10 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-white/5 border-b border-white/10">
                  <th className="text-left px-4 py-3 font-semibold">Restaurant</th>
                  <th className="text-left px-4 py-3 font-semibold w-48">Organisatie</th>
                  {MODULES.map(m => (
                    <th key={m.key} className="text-center px-3 py-3 font-medium text-xs text-white/70">
                      <div className="inline-flex items-center gap-2 justify-center">
                        <m.icon size={14} /> {m.label}
                      </div>
                      <div className="text-[10px] text-white/40 font-normal mt-0.5">{m.sub}</div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map(row => (
                  <tr key={row.restaurant_id}
                    className="border-b border-white/5 hover:bg-white/[0.02]"
                    data-testid={`modules-row-${row.restaurant_id}`}>
                    <td className="px-4 py-3 font-medium">{row.restaurant_name}</td>
                    <td className="px-4 py-3 text-white/60 text-xs">{row.organization_name || "—"}</td>
                    {MODULES.map(m => {
                      const busy = !!saving[`${row.restaurant_id}:${m.key}`];
                      return (
                        <td key={m.key} className="px-3 py-3 text-center">
                          <div className="flex items-center justify-center gap-1">
                            <Toggle checked={!!row[m.key]} onChange={next => toggle(row, m.key, next)}
                              disabled={busy}
                              testid={`module-toggle-${row.restaurant_id}-${m.key}`} />
                            {busy && <Loader2 size={12} className="animate-spin text-white/40" />}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr><td colSpan={6} className="text-center py-8 text-white/50">Geen restaurants gevonden</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
